import React, { useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFaceSadTear } from "@fortawesome/free-regular-svg-icons";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";

import NavBar from "../components/common/navBar";

import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeIn = keyframes`
	from {
		opacity: 0;
		transform: scale(0.9);
	}
	to {
		opacity: 1;
		transform: scale(1);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	display: flex;
	flex-direction: column;
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div`
	flex: 1;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 2rem;
`;

const NotFoundContainer = styled.div`
	text-align: center;
	animation: ${fadeIn} 0.6s ease-out;
`;

const NotFoundTitle = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.display.fontSize};
	font-weight: ${props => props.theme.typography.roles.display.fontWeight};
	line-height: ${props => props.theme.typography.roles.display.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.display.letterSpacing};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 1.5rem;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 1rem;

	svg {
		color: ${props => props.theme.colors.primary.main};
	}

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const NotFoundMessage = styled.p<{ theme: Theme }>`
	font-size: 1.25rem;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.8;
	margin-bottom: 2rem;
	max-width: 600px;

	@media (max-width: 768px) {
		font-size: 1.125rem;
	}
`;

const BackButton = styled.a<{ theme: Theme }>`
	display: inline-block;
	padding: 1rem 2rem;
	background: ${props => props.theme.colors.primary.gradient};
	color: white;
	font-weight: 600;
	border-radius: ${props => props.theme.borderRadius.lg};
	text-decoration: none;
	transition: all ${props => props.theme.transitions.base};
	box-shadow: ${props => props.theme.shadows.md};

	&:hover {
		box-shadow: ${props => props.theme.shadows.glowHover};
		transform: translateY(-2px);
	}

	&:active {
		transform: translateY(0);
	}
`;

const Notfound = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const userInfo = useUserInfo();
	useEffect(() => {
		document.title = `404 | ${userInfo.main.title}`;
	}, [userInfo.main.title]);

	return (
		<React.Fragment>
			<PageContainer theme={theme}>
				<NavBar />
				<ContentWrapper>
					<NotFoundContainer>
						<NotFoundTitle theme={theme}>
							{t.notFound.title} <FontAwesomeIcon icon={faFaceSadTear} />
						</NotFoundTitle>
						<NotFoundMessage theme={theme}>
							{t.notFound.description}
						</NotFoundMessage>
						<BackButton theme={theme} href="/">
							{t.notFound.backToHome}
						</BackButton>
					</NotFoundContainer>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default Notfound;
